/**
 * The register record's Graph I/O (13-23): read one record and its sibling rows, create a record (the lodge),
 * commit a verb, and post the audit rows a write still owes. Every panel (13-24 … 13-28, 13-32) calls these through
 * the Writer seam (`commitAdapter.ts`), never directly.
 *
 *   - **Read** (`getRecord`): one item GET with `$expand=fields($select=<every field-set column>,Created,Modified)`,
 *     the selection INSIDE the expand (a `$select=fields/X` read 400s, and a swallowed 400 reads as empty). Person
 *     columns are read by `…LookupId` only (D-13-07-7). Then `listSiblings`: the register's audit sibling,
 *     `ParentRef eq '<ref>'`, paged on `@odata.nextLink`, oldest first.
 *   - **Create** (`createRecord`, D-13-L-8): the parent with `buildLodge`'s fields (POST, retried on 429 only,
 *     D-13-07-1), then each sibling draft with `ParentRef` = `<PREFIX>-<new id>` composed from the returned id
 *     (ISS-008), `ActorUPN`, `ActorLookupId` when resolved, and its `[a:<id>]` tag in the Note.
 *   - **Commit** (`commitRecordVerb`, D-13-L-4): the authority gate again (D-13-07-6), then PATCH with `If-Match`
 *     and only the verb's patch columns, then the sibling drafts. A 412 re-reads the record. The change is adopted
 *     only if every patch value is already on the row AND Graph names this actor as its last editor (D-13-07-2,
 *     D-13-23-4); otherwise it throws `RecordConflictError` with the fresh row and the columns that differ. It
 *     never merges. A 404 is a conflict.
 *   - **Owed audit rows** (`postRecordAudit`, D-13-L-5, D-13-07-3): one fresh sibling read, then only the drafts
 *     whose `[a:<id>]` tag (with `FieldChanged`, or `LogKind` on a non-`StateChange` row) is not already there. A
 *     failed read posts nothing. An untagged draft is never posted blind.
 *
 * Retry policy: `graphClient.withRetry` only; the SDK's own retry is off on every request here (D-13-07-1). Text
 * columns are written as plain text, the rich-text `Description`, `ClosingComments` and CI `Issue` included
 * (D-13-L-18); the record page keeps their line breaks (CSS `pre-wrap`). No React.
 */

import {
  createItemFields,
  errorStatus,
  eTagOf,
  getItemEnvelope,
  getListId,
  listItemEnvelopes,
  updateItemFields,
} from './graphClient';
import type { GraphListItemEnvelope, RetryOptions } from './graphClient';
import { CommitError, patchAlreadyApplied } from './actionService';
import type { ActionIO } from './actionService';
import { REGISTERS } from '../config/registers';
import { lodgeColumns } from '../config/fieldset/registerFields';
import { LODGE_CONSTANTS, lodgeMayWrite } from '../config/fieldset/floor';
import { liveChoice } from '../config/fieldset/live';
import { SIBLING_FIELDS } from '../config/fieldset/siblings';
import { canDoRecordVerb } from '../lib/recordAuthority';
import type { RecordAuthorityContext, RecordAuthorityReason } from '../lib/recordAuthority';
import { actionTag, noteHasAction } from '../lib/outbox';
import {
  RecordRefError,
  composeRef,
  DUPLICATE_READS,
  duplicateFilter,
  listsOf,
  parseRef,
  PREPARER_SELECT,
  preparerFilter,
  RECORD_CONTEXT_READS,
  recordSelect,
  SIBLING_SELECT,
  siblingFilter,
  waitingSelect,
} from '../lib/recordQuery';
import type { AuditEntryType } from '../types/qms';
import type { Actor } from '../types/actionRegister';
import type { LodgeResult, RecordRegister, RecordRow, RecordVerbKey, SiblingDraft, VerbResult } from '../types/records';

export { RecordRefError, parseRef };

// ---------------------------------------------------------------------------
// I/O context
// ---------------------------------------------------------------------------

/**
 * The Action Register's I/O context, plus (optionally) the list ids `useGraphClient` resolved. A list id that is
 * not given is resolved by its title through `getListId`, whose cache `useGraphClient` has already filled.
 */
export interface RecordIO extends ActionIO {
  listIds?: Readonly<Partial<Record<string, string>>>;
}

async function listIdOf(io: RecordIO, register: RecordRegister, which: 'parent' | 'audit'): Promise<string> {
  const l = listsOf(register);
  const given = io.listIds?.[which === 'parent' ? l.idKey : l.auditIdKey];
  if (given) return given;
  return getListId(io.client, io.siteId, which === 'parent' ? l.title : l.auditTitle);
}

/** Every POST here is not idempotent: retried in-call on a 429 only (refused before processing, D-13-07-1). */
function postRetry(io: ActionIO): RetryOptions {
  return { ...io.retry, retryOn: [429] };
}

// ---------------------------------------------------------------------------
// Mapping (Graph omits an empty field entirely, so a missing key means null)
// ---------------------------------------------------------------------------

type Fields = Record<string, unknown>;

function str(f: Fields, key: string): string | null {
  const v = f[key];
  if (v === undefined || v === null || v === '') return null;
  return typeof v === 'string' ? v : String(v);
}

/** The lower-cased email of a Graph `createdBy` / `lastModifiedBy` (13-32 reads it for Internal Audits too). */
export function emailOf(user: { user?: { email?: string } } | undefined): string | null {
  const e = user?.user?.email;
  return typeof e === 'string' && e.trim() !== '' ? e.trim().toLowerCase() : null;
}

/** A Graph register item → `RecordRow` (the composed ref, never `Reference`; the item eTag for `If-Match`). */
export function mapRecordItem(register: RecordRegister, item: GraphListItemEnvelope): RecordRow {
  const id = Number(item.id);
  const fields: Fields = { ...(item.fields ?? {}) };
  delete fields['@odata.etag'];
  return {
    id,
    ref: composeRef(register, id),
    eTag: eTagOf(item) ?? str(item.fields ?? {}, '@odata.etag') ?? '',
    register,
    fields,
  };
}

/** One audit-sibling row as read (FIELDSET §2.8). `authorEmail` is the item's `createdBy`: who really wrote it. */
export interface SiblingRow {
  id: number;
  title: string | null;
  parentRef: string;
  entryTimestamp: string | null;
  entryType: AuditEntryType | null;
  logKind: string | null;
  fieldChanged: string | null;
  oldValue: string | null;
  newValue: string | null;
  note: string | null;
  actorUpn: string | null;
  actorLookupId: string | null;
  authorEmail: string | null;
  created: string | null;
}

export function mapSiblingItem(item: GraphListItemEnvelope): SiblingRow {
  const f: Fields = item.fields ?? {};
  const S = SIBLING_FIELDS;
  return {
    id: Number(item.id),
    title: str(f, S.title),
    parentRef: str(f, S.parentRef) ?? '',
    entryTimestamp: str(f, S.entryTimestamp),
    entryType: str(f, S.entryType) as AuditEntryType | null,
    logKind: str(f, S.logKind),
    fieldChanged: str(f, S.fieldChanged),
    oldValue: str(f, S.oldValue),
    newValue: str(f, S.newValue),
    note: str(f, S.note),
    actorUpn: str(f, S.actorUPN),
    actorLookupId: str(f, S.actorLookupId),
    authorEmail: emailOf(item.createdBy),
    created: item.createdDateTime ?? null,
  };
}

function timeOf(r: SiblingRow): number {
  const t = Date.parse(r.entryTimestamp ?? r.created ?? '');
  return Number.isNaN(t) ? 0 : t;
}

/** Oldest first: `EntryTimestamp`, then the item id. */
export function sortSiblings(rows: readonly SiblingRow[]): SiblingRow[] {
  return [...rows].sort((a, b) => timeOf(a) - timeOf(b) || a.id - b.id);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * The sibling rows of one record, oldest first: `ParentRef eq '<ref>'` with the non-indexed Prefer header, paged on
 * `@odata.nextLink`. Sorted here, not by `$orderby` (D-13-23-5: the filter is the shape proven live). An error is
 * thrown, never swallowed: an empty answer must mean "no rows".
 */
export async function listSiblings(io: RecordIO, ref: string): Promise<SiblingRow[]> {
  const { register, ref: canonical } = parseRef(ref);
  const listId = await listIdOf(io, register, 'audit');
  const items = await listItemEnvelopes(io.client, io.siteId, listId, {
    select: SIBLING_SELECT,
    filter: siblingFilter(canonical),
    retry: io.retry,
  });
  return sortSiblings(items.map(mapSiblingItem).filter((r) => r.parentRef === canonical));
}

async function readRecordItem(io: RecordIO, register: RecordRegister, id: number): Promise<GraphListItemEnvelope> {
  const listId = await listIdOf(io, register, 'parent');
  return getItemEnvelope(io.client, io.siteId, listId, id, recordSelect(register), io.retry);
}

/** What the record page reads: the row, its siblings oldest first, and who last edited it. */
export interface RecordRead {
  row: RecordRow;
  siblings: SiblingRow[];
  /** `lastModifiedBy` (lower-cased), when Graph reports it. */
  modifiedBy: string | null;
  /** The latest `Status → Evaluating` row's `ActorUPN` (D-13-L-19). */
  preparedByUpn: string | undefined;
}

/** One record by its composed ref, then its sibling rows. Either read failing throws (a 404 keeps its status). */
export async function getRecord(io: RecordIO, ref: string): Promise<RecordRead> {
  const { register, id, ref: canonical } = parseRef(ref);
  const item = await readRecordItem(io, register, id);
  const row = mapRecordItem(register, item);
  const siblings = await listSiblings(io, canonical);
  return { row, siblings, modifiedBy: emailOf(item.lastModifiedBy), preparedByUpn: preparedByUpn(siblings) };
}

/**
 * 13.2-10: one record's row only (the item, every field-set column), with no sibling read: the kept record a duplicate
 * is closed against (`ctx.kept`). A 404 keeps its status, so the caller can tell "no such record" from a failure.
 */
export async function getRecordRow(io: RecordIO, ref: string): Promise<RecordRow> {
  const { register, id } = parseRef(ref);
  return mapRecordItem(register, await readRecordItem(io, register, id));
}

/**
 * A few context columns of one live record, read only (13-27: the C3 draft beside the reportability call,
 * `RECORD_CONTEXT_READS`). A separate, short item GET, so the record read itself stays under the 2,048-character
 * limit (ISS-261). Only columns named in `RECORD_CONTEXT_READS` for the register may be asked for. Never writes.
 */
export async function getRecordColumns(io: RecordIO, ref: string, columns: readonly string[]): Promise<Record<string, unknown>> {
  const { register, id } = parseRef(ref);
  const allowed = RECORD_CONTEXT_READS[register] ?? [];
  const unknown = columns.filter((c) => !allowed.includes(c));
  if (columns.length === 0 || unknown.length > 0) throw new Error(`getRecordColumns: not context columns of ${register}: ${unknown.join(', ') || '(none asked)'}`);
  const listId = await listIdOf(io, register, 'parent');
  const item = await getItemEnvelope(io.client, io.siteId, listId, id, columns, io.retry);
  const fields: Record<string, unknown> = { ...(item.fields ?? {}) };
  delete fields['@odata.etag'];
  return fields;
}

// ---------------------------------------------------------------------------
// Today's reads (13-29): "Records waiting on you"
// ---------------------------------------------------------------------------

/**
 * Every item of one register with only the columns `recordsWaitingOn` reads (`recordQuery.waitingSelect`), paged on
 * `@odata.nextLink`: one list read per register the lens touches, never a read per row (ISS-207). An error is
 * thrown, never swallowed: an empty answer must mean "no records" (a swallowed 4xx reads as empty, 13-23).
 */
export async function listWaitingRecords(io: RecordIO, register: RecordRegister): Promise<RecordRow[]> {
  const listId = await listIdOf(io, register, 'parent');
  const items = await listItemEnvelopes(io.client, io.siteId, listId, { select: waitingSelect(register), retry: io.retry });
  return items.map((item) => mapRecordItem(register, item));
}

/**
 * 13.2-10: the possible-duplicate candidates, ONE paged read of Incidents with only `DUPLICATE_READS`, filtered on
 * `ReportedOn` within `window` (`duplicates.candidateWindow`: the row's day ± 7 Melbourne days). Errors are thrown,
 * never swallowed: an empty answer must mean "no candidates".
 */
export async function listIncidentsNear(io: RecordIO, window: { from: string; to: string }): Promise<RecordRow[]> {
  const listId = await listIdOf(io, 'incidents', 'parent');
  const items = await listItemEnvelopes(io.client, io.siteId, listId, {
    select: DUPLICATE_READS,
    filter: duplicateFilter(window.from, window.to),
    retry: io.retry,
  });
  return items.map((item) => mapRecordItem('incidents', item));
}

/**
 * Each Incident's preparer from ONE read of the Incidents Audit Log (`Status → Evaluating` rows only, non-indexed,
 * with the Prefer header), so Oversight's lens can offer approve-and-close without a sibling read per incident.
 * Each ref's preparer is `preparedByUpn` of its rows (the latest one), exactly as the record page reads it.
 */
export async function listIncidentPreparers(io: RecordIO): Promise<Record<string, string | undefined>> {
  const readied = await listIncidentReadied(io);
  return Object.fromEntries(Object.entries(readied).map(([ref, r]) => [ref, r.upn]));
}

/**
 * 13.2-08: the same ONE read as `listIncidentPreparers`, with each incident's readier AND when they readied it
 * (`readiedBy`), for Verify's "Incidents to close" ("Readied by … on …").
 */
export async function listIncidentReadied(io: RecordIO): Promise<Record<string, Readied>> {
  const listId = await listIdOf(io, 'incidents', 'audit');
  const items = await listItemEnvelopes(io.client, io.siteId, listId, {
    select: PREPARER_SELECT,
    filter: preparerFilter(),
    retry: io.retry,
  });
  const byRef = new Map<string, SiblingRow[]>();
  for (const row of items.map(mapSiblingItem)) {
    if (row.parentRef === '') continue;
    byRef.set(row.parentRef, [...(byRef.get(row.parentRef) ?? []), row]);
  }
  const out: Record<string, Readied> = {};
  for (const [ref, rows] of byRef) {
    const r = readiedBy(rows);
    if (r) out[ref] = r;
  }
  return out;
}

// ---------------------------------------------------------------------------
// The preparer (D-13-L-19)
// ---------------------------------------------------------------------------

const STATUS = REGISTERS.incidents.fields.status;
const [EVALUATING] = liveChoice('incidents', STATUS, 'Evaluating');
const STATE_CHANGE: AuditEntryType = 'StateChange';

const same = (a: string | null | undefined, b: string) => (a ?? '').trim().toLowerCase() === b.toLowerCase();

/**
 * Who ran *ready for approval*: the `ActorUPN` of the latest `StateChange` row with `FieldChanged = Status` and
 * `NewValue = Evaluating` (compared ignoring case), else that row's Author, else `undefined`. Lower-cased. No
 * other email in a Note is ever used (the D-13-07-5 rule). 13-21 and 13-22 read it as `ctx.preparedByUpn`.
 */
export function preparedByUpn(siblings: readonly SiblingRow[]): string | undefined {
  return readiedBy(siblings)?.upn;
}

/** Who readied an incident (as `preparedByUpn`) and when (the row's `EntryTimestamp`, else its `Created`). */
export interface Readied {
  upn: string | undefined;
  at: string | null;
}

/**
 * 13.2-08: the latest `Status → Evaluating` row as `{ upn, at }`, or `undefined` when there is none. `upn` is
 * exactly `preparedByUpn`'s answer (which is built on this), so the two never disagree.
 */
export function readiedBy(siblings: readonly SiblingRow[]): Readied | undefined {
  const rows = sortSiblings(
    siblings.filter(
      (r) => same(r.entryType, STATE_CHANGE) && same(r.fieldChanged, STATUS) && same(r.newValue, EVALUATING),
    ),
  );
  const latest = rows.at(-1);
  if (!latest) return undefined;
  const at = latest.entryTimestamp ?? latest.created ?? null;
  const upn = (latest.actorUpn ?? '').trim().toLowerCase();
  if (upn !== '') return { upn, at };
  return { upn: latest.authorEmail ?? undefined, at };
}

// ---------------------------------------------------------------------------
// Sibling drafts → posted rows
// ---------------------------------------------------------------------------

/** A draft → the SP fields of a new sibling item. `auditId` is not a column; empty values are omitted. */
export function siblingFields(draft: SiblingDraft): Record<string, unknown> {
  const S = SIBLING_FIELDS;
  const out: Record<string, unknown> = {};
  const put = (key: string, v: unknown) => {
    if (v !== undefined && v !== null && v !== '') out[key] = v;
  };
  put(S.title, draft.Title);
  put(S.parentRef, draft.ParentRef);
  put(S.entryType, draft.EntryType);
  put(S.logKind, draft.LogKind);
  put(S.fieldChanged, draft.FieldChanged);
  put(S.oldValue, draft.OldValue);
  put(S.newValue, draft.NewValue);
  put(S.note, draft.Note);
  put(S.entryTimestamp, draft.EntryTimestamp);
  put(S.actorUPN, draft.ActorUPN);
  put(S.actorLookupId, draft.ActorLookupId);
  return out;
}

/**
 * A draft ready to post under `ref` (D-13-L-8): `ParentRef` = the composed ref; a lodge `Title` gains the ref
 * (`lodge INC-42: Status`, D-13-19-6); `ActorUPN` = the actor when the builder left it blank; `ActorLookupId` only
 * when the site-user id resolved AND the row is the actor's own (an investigation step may name another
 * investigator, 13-20); the `[a:<auditId>]` tag appended to the Note unless the builder already wrote it.
 */
export function prepareSibling(draft: SiblingDraft, ref: string, actor: Pick<Actor, 'upn' | 'lookupId'>): SiblingDraft {
  const actorUpn = (actor.upn ?? '').trim().toLowerCase();
  const rowUpn = (draft.ActorUPN ?? '').trim() === '' ? actorUpn : draft.ActorUPN.trim().toLowerCase();
  const tag = draft.auditId ? actionTag(draft.auditId) : '';
  const note = draft.Note ?? '';
  const out: SiblingDraft = {
    ...draft,
    Title: /^lodge:/.test(draft.Title) ? draft.Title.replace(/^lodge:/, `lodge ${ref}:`) : draft.Title,
    ParentRef: ref,
    ActorUPN: rowUpn,
    Note: tag && !note.includes(tag) ? (note ? `${note} ${tag}` : tag) : note,
  };
  if (!out.ActorLookupId && actor.lookupId && rowUpn === actorUpn) out.ActorLookupId = actor.lookupId;
  return out;
}

/** The de-dupe key (D-13-07-3): the tag, plus `FieldChanged` on a `StateChange` row or `LogKind` on any other. */
export function alreadyPosted(existing: readonly SiblingRow[], draft: SiblingDraft): boolean {
  return existing.some((r) => {
    if (!noteHasAction(r.note, draft.auditId)) return false;
    if ((draft.EntryType ?? '') === STATE_CHANGE) return (r.fieldChanged ?? '') === (draft.FieldChanged ?? '');
    return (r.entryType ?? '') === (draft.EntryType ?? '') && (r.logKind ?? '') === (draft.LogKind ?? '');
  });
}

/** Post drafts in order; the first failure leaves it and every later draft owed (never retried blind). */
export async function postInOrder(io: RecordIO, auditList: string, drafts: readonly SiblingDraft[]): Promise<SiblingDraft[]> {
  for (let i = 0; i < drafts.length; i += 1) {
    try {
      await createItemFields(io.client, io.siteId, auditList, siblingFields(drafts[i]), postRetry(io));
    } catch {
      return drafts.slice(i);
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** The authority gate said no (D-13-07-6). Thrown before any Graph call. */
export class RecordRefusedError extends Error {
  readonly reason: RecordAuthorityReason;
  readonly verb: RecordVerbKey;

  constructor(verb: RecordVerbKey, reason: RecordAuthorityReason) {
    super(`${verb} refused: ${reason}`);
    this.name = 'RecordRefusedError';
    this.verb = verb;
    this.reason = reason;
  }
}

/**
 * The PATCH met a changed record (412) or a deleted one (404, `current` null). `columns`: the patch columns whose
 * fresh value differs from the patch. `changedSinceRead`: every column whose fresh value differs from the row the
 * caller started from. `VerbForm` (13-25) shows both and asks again; nothing was merged (D-13-L-4).
 */
export class RecordConflictError extends CommitError {
  readonly current: RecordRow | null;
  readonly columns: string[];
  readonly changedSinceRead: string[];

  constructor(cause: unknown, current: RecordRow | null, columns: string[], changedSinceRead: string[]) {
    super('patch', cause, [], 412);
    this.name = 'RecordConflictError';
    this.current = current;
    this.columns = columns;
    this.changedSinceRead = changedSinceRead;
  }
}

// ---------------------------------------------------------------------------
// The authority gate in the service (D-13-07-6, 13-22's hand-off)
// ---------------------------------------------------------------------------

/** What the gate needs: the verb, the row the caller built it from, and the facts the caller resolved. */
export interface RecordGate {
  verb: RecordVerbKey;
  row: RecordRow;
  ctx?: RecordAuthorityContext;
}

/** Throws `RecordRefusedError` unless `canDoRecordVerb` allows it; the row must be the record being written. */
export function checkGate(gate: RecordGate, actor: Actor, ref?: string): void {
  if (!gate || !gate.row) throw new RecordRefusedError(gate?.verb ?? 'lodge', 'unknown-verb');
  if (ref !== undefined && gate.row.ref !== ref) {
    throw new Error(`recordService: the gate's row ${gate.row.ref} is not ${ref}`);
  }
  const allowed = canDoRecordVerb(gate.verb, gate.row, actor, gate.ctx ?? {});
  if (!allowed.ok) throw new RecordRefusedError(gate.verb, allowed.reason);
}

/** A row-shaped stand-in for the lodge gate (the lodge rule reads only the register). */
function lodgeGateRow(register: RecordRegister): RecordRow {
  return { id: 0, ref: '', eTag: '', register, fields: {} };
}

/** The lodge payload holds only §3.2 columns and the two lodge constants (§1.3; `buildLodge` built it so). Throws otherwise. */
export function assertLodgeFields(register: RecordRegister, fields: Record<string, unknown>): void {
  const allowed = new Set<string>([...lodgeColumns(register), ...Object.keys(LODGE_CONSTANTS[register])]);
  for (const [k, v] of Object.entries(fields)) {
    if (!allowed.has(k) || !lodgeMayWrite(register, k, v)) {
      throw new Error(`recordService: ${register}.${k} may not be written by a lodge (authority floor)`);
    }
  }
}

// ---------------------------------------------------------------------------
// Create (the lodge)
// ---------------------------------------------------------------------------

export interface CreateRecordResult {
  ref: string;
  id: number;
  eTag?: string;
  /** Sibling drafts that did not post after the parent landed (owed, D-13-L-5). Never dropped. */
  auditPending: SiblingDraft[];
}

/**
 * Create the parent with `lodge.fields`, then its sibling drafts under the composed `ParentRef`. A failed create
 * throws `CommitError('patch')`: nothing was written that we know of. A sibling that fails after the parent
 * landed, and every later one, is returned in `auditPending`.
 */
export async function createRecord(
  io: RecordIO,
  register: RecordRegister,
  lodge: LodgeResult,
  actor: Actor,
): Promise<CreateRecordResult> {
  checkGate({ verb: 'lodge', row: lodgeGateRow(register) }, actor);
  assertLodgeFields(register, lodge.fields);
  const parentList = await listIdOf(io, register, 'parent');
  let created: { id: string; eTag?: string };
  try {
    created = await createItemFields(io.client, io.siteId, parentList, lodge.fields, postRetry(io));
  } catch (err) {
    throw new CommitError('patch', err);
  }
  const id = Number(created.id);
  if (!Number.isSafeInteger(id) || id < 1) {
    throw new CommitError('patch', new Error('the create returned no item id: check the register before lodging again'));
  }
  const ref = composeRef(register, id);
  const drafts = lodge.siblings.map((d) => prepareSibling(d, ref, actor));
  const auditList = await listIdOf(io, register, 'audit').catch(() => '');
  const auditPending = auditList ? await postInOrder(io, auditList, drafts) : drafts;
  return { ref, id, ...(created.eTag ? { eTag: created.eTag } : {}), auditPending };
}

// ---------------------------------------------------------------------------
// Commit a verb
// ---------------------------------------------------------------------------

export interface CommitRecordResult {
  /** The item's new eTag (for the next save on this record), when Graph returned one. */
  eTag?: string;
  /** Sibling drafts that did not post after the PATCH landed (owed, D-13-L-5). */
  auditPending: SiblingDraft[];
  /** A 412 whose change was found to be ours (a crash between the PATCH and its reply, D-13-07-2). */
  adopted: boolean;
}

/** Each patch column whose value on `fields` differs (`patchAlreadyApplied`'s comparison, column by column). */
function differingColumns(fields: Fields, patch: Record<string, unknown>): string[] {
  return Object.keys(patch).filter((k) => !patchAlreadyApplied({ id: '', fields }, { [k]: patch[k] }));
}

/** Columns of `fresh` that differ from the row the caller read (the other person's change). */
function changedSince(read: RecordRow | undefined, fresh: RecordRow): string[] {
  if (!read) return [];
  const keys = new Set([...Object.keys(read.fields), ...Object.keys(fresh.fields)]);
  keys.delete('Modified');
  return [...keys].filter((k) => !patchAlreadyApplied({ id: '', fields: fresh.fields }, { [k]: read.fields[k] }));
}

/**
 * Ours only if every patch value is on the row AND Graph names this actor as the last editor. Stricter than the
 * Action Register (D-13-07-2 accepts a missing editor): on a register, an unknown editor is a conflict (D-13-23-4).
 */
function changeIsOurs(item: GraphListItemEnvelope, patch: Record<string, unknown>, actorUpn: string): boolean {
  const editor = emailOf(item.lastModifiedBy);
  if (!editor || editor !== actorUpn.trim().toLowerCase()) return false;
  return patchAlreadyApplied(item, patch, actorUpn);
}

/**
 * Commit one verb save. The gate first (nothing is sent on a refusal). Then, if the patch has columns, PATCH with
 * `If-Match` and only those columns; an empty patch (a rows-only save, 13-20) skips the PATCH. Then the drafts.
 */
export async function commitRecordVerb(
  io: RecordIO,
  ref: string,
  eTag: string,
  result: VerbResult,
  actor: Actor,
  gate: RecordGate,
): Promise<CommitRecordResult> {
  const { register, id, ref: canonical } = parseRef(ref);
  checkGate(gate, actor, canonical);
  if (gate.row.register !== register) throw new Error(`recordService: the gate's row is not on ${register}`);
  const drafts = result.siblings.map((d) => prepareSibling(d, canonical, actor));
  const patch: Record<string, unknown> = { ...result.patch };
  let newETag: string | undefined;
  let adopted = false;
  let existing: SiblingRow[] | null = null;

  if (Object.keys(patch).length > 0) {
    if (!eTag) throw new CommitError('patch', new Error('an eTag is required (If-Match)'));
    const parentList = await listIdOf(io, register, 'parent');
    try {
      ({ eTag: newETag } = await updateItemFields(io.client, io.siteId, parentList, id, patch, eTag, io.retry));
    } catch (err) {
      const status = errorStatus(err);
      if (status === 404) throw new RecordConflictError(err, null, Object.keys(patch), []);
      if (status !== 412) throw new CommitError('patch', err);
      let item: GraphListItemEnvelope;
      try {
        item = await readRecordItem(io, register, id);
      } catch (readErr) {
        if (errorStatus(readErr) === 404) throw new RecordConflictError(err, null, Object.keys(patch), []);
        // Unknown is not a conflict: nothing was decided, the person can save again.
        throw new CommitError('patch', readErr);
      }
      const current = mapRecordItem(register, item);
      if (!changeIsOurs(item, patch, actor.upn)) {
        throw new RecordConflictError(err, current, differingColumns(current.fields, patch), changedSince(gate.row, current));
      }
      adopted = true;
      newETag = current.eTag || undefined;
      try {
        existing = await listSiblings(io, canonical);
      } catch {
        // The change is ours but the trail can't be read: post nothing, owe every draft (D-13-07-3).
        return { ...(newETag ? { eTag: newETag } : {}), auditPending: drafts, adopted };
      }
    }
  }

  const toPost = existing ? drafts.filter((d) => !alreadyPosted(existing as SiblingRow[], d)) : drafts;
  let auditPending: SiblingDraft[] = [];
  if (toPost.length > 0) {
    const auditList = await listIdOf(io, register, 'audit').catch(() => '');
    auditPending = auditList ? await postInOrder(io, auditList, toPost) : toPost;
  }
  return { ...(newETag ? { eTag: newETag } : {}), auditPending, adopted };
}

// ---------------------------------------------------------------------------
// Owed audit rows (D-13-L-5)
// ---------------------------------------------------------------------------

/**
 * Post drafts a write still owes for `ref`, de-duped against ONE fresh sibling read. Returns the drafts still owed
 * (empty = the trail is complete): every draft when the read fails; an untagged draft always (never posted blind);
 * the first draft whose POST fails and every later one.
 */
export async function postRecordAudit(io: RecordIO, ref: string, drafts: readonly SiblingDraft[]): Promise<SiblingDraft[]> {
  if (drafts.length === 0) return [];
  const { register, ref: canonical } = parseRef(ref);
  const withRef = drafts.map((d) => ({ ...d, ParentRef: canonical }));
  const untagged = withRef.filter((d) => !d.auditId || !(d.Note ?? '').includes(actionTag(d.auditId)));
  const tagged = withRef.filter((d) => !untagged.includes(d));
  if (tagged.length === 0) return untagged;
  let existing: SiblingRow[];
  try {
    existing = await listSiblings(io, canonical);
  } catch {
    return withRef;
  }
  const toPost = tagged.filter((d) => !alreadyPosted(existing, d));
  if (toPost.length === 0) return untagged;
  let auditList: string;
  try {
    auditList = await listIdOf(io, register, 'audit');
  } catch {
    return [...untagged, ...toPost];
  }
  return [...untagged, ...(await postInOrder(io, auditList, toPost))];
}

